import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Users, Layers, ClipboardList, Rocket, PieChart, Calendar,
  MessageSquare, FolderOpen, Building2, BarChart3, LogOut, Settings, Activity, FileInput, Target, HelpCircle, PanelLeftClose, PanelLeft, Bot,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrg } from "@/contexts/OrgContext";

interface NavItem {
  label: string;
  icon: React.ElementType;
  href: string;
}

const adminNav: NavItem[] = [
  { label: "Home", icon: Home, href: "/admin" },
  { label: "Suppliers", icon: Users, href: "/admin/directory" },
  { label: "Categories", icon: Layers, href: "/admin/segments" },
  { label: "Assessments", icon: FileInput, href: "/admin/applications" },
  { label: "Tools", icon: ClipboardList, href: "/admin/tools" },
  { label: "Deploy", icon: Rocket, href: "/admin/deploy" },
  { label: "Plans", icon: Target, href: "/admin/plans" },
  { label: "Results", icon: PieChart, href: "/admin/results" },
  { label: "Reporting", icon: BarChart3, href: "/admin/reporting" },
  { label: "Engagement", icon: Activity, href: "/admin/tracking" },
  { label: "Events", icon: Calendar, href: "/admin/events" },
  { label: "Resources", icon: FolderOpen, href: "/admin/resources" },
  { label: "AI Assistant", icon: Bot, href: "/admin/help" },
];

const supplierNav: NavItem[] = [
  { label: "Home", icon: Home, href: "/supplier" },
  { label: "My Profile", icon: Building2, href: "/supplier/profile" },
  { label: "Assigned Tools", icon: ClipboardList, href: "/supplier/tools" },
  { label: "Performance", icon: BarChart3, href: "/supplier/performance" },
  { label: "Plans", icon: Target, href: "/supplier/plans" },
  { label: "Community", icon: MessageSquare, href: "/supplier/community" },
  { label: "Events", icon: Calendar, href: "/supplier/events" },
  { label: "Resources", icon: FolderOpen, href: "/supplier/resources" },
  { label: "AI Assistant", icon: Bot, href: "/supplier/help" },
];

interface AppSidebarProps {
  role: "admin" | "supplier";
  collapsed: boolean;
  onToggle: () => void;
}

export const AppSidebar = ({ role, collapsed, onToggle }: AppSidebarProps) => {
  const location = useLocation();
  const { signOut, user, role: actualRole } = useAuth();
  const { org } = useOrg();
  const nav = role === "admin" ? adminNav : supplierNav;
  const [companyName, setCompanyName] = useState<string | null>(null);

  useEffect(() => {
    if (!user || actualRole !== "supplier") return;
    supabase
      .from("profiles")
      .select("company_name")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => setCompanyName(data?.company_name || null));
  }, [user, actualRole]);

  const sidebarWidth = collapsed ? "w-16" : "w-56";

  return (
    <>
      {/* Mobile overlay */}
      {!collapsed && (
        <div
          className="fixed inset-0 z-30 bg-black/20 lg:hidden"
          onClick={onToggle}
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-border bg-card transition-all duration-300",
          sidebarWidth
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between pr-1">
          {collapsed ? (
            <div className="flex w-full items-center justify-center py-5">
              {org?.logo_url ? (
                <img src={org.logo_url} alt={org.name} className="h-8 w-8 rounded-xl object-cover" />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary">
                  <Building2 className="h-4 w-4 text-primary-foreground" />
                </div>
              )}
            </div>
          ) : (
            <Link to={actualRole === "admin" ? "/admin/settings" : "#"} className="flex flex-1 items-center gap-3 px-5 py-5 hover:bg-secondary/50 transition-colors min-w-0">
              {org?.logo_url ? (
                <img src={org.logo_url} alt={org.name} className="h-9 w-9 rounded-xl object-cover shrink-0" />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary shrink-0">
                  <Building2 className="h-5 w-5 text-primary-foreground" />
                </div>
              )}
              <div className="min-w-0">
                <span className="block font-display text-sm font-bold text-foreground tracking-tight truncate">
                  {org?.name || "My Ecosystem"}
                </span>
                <span className="block text-[10px] text-muted-foreground">Powered by Cadena Zero</span>
              </div>
            </Link>
          )}
        </div>

        {/* Toggle button */}
        <div className={cn("px-3 pb-1", collapsed && "flex justify-center px-0")}>
          <button
            onClick={onToggle}
            className={cn(
              "flex items-center justify-center rounded-md hover:bg-secondary transition-colors text-muted-foreground",
              collapsed ? "h-8 w-8" : "h-7 w-7 ml-auto"
            )}
            title={collapsed ? "Expand menu" : "Collapse menu"}
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className={cn("flex-1 space-y-0.5 overflow-y-auto pt-1", collapsed ? "px-2" : "px-3")}>
          {nav.map((item) => {
            const isActive =
              item.href === "/admin" || item.href === "/supplier"
                ? location.pathname === item.href
                : location.pathname.startsWith(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                title={collapsed ? item.label : undefined}
                className={cn(
                  "flex items-center rounded-lg text-sm font-medium transition-colors",
                  collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2",
                  isActive
                    ? "bg-accent/10 text-foreground font-semibold"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )}
              >
                <item.icon className="h-[18px] w-[18px] shrink-0" />
                {!collapsed && item.label}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-border p-3">
          {collapsed ? (
            <>
              <Link
                to={actualRole === "admin" ? "/admin/settings" : "#"}
                className="flex items-center justify-center py-1.5 mb-1 rounded-lg hover:bg-secondary transition-colors"
                title={companyName || user?.email || "Profile"}
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {(user?.email || "?").charAt(0).toUpperCase()}
                </div>
              </Link>
              <button
                onClick={signOut}
                className="flex w-full items-center justify-center rounded-lg py-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                title="Sign Out"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </button>
            </>
          ) : (
            <>
              <Link
                to={actualRole === "admin" ? "/admin/settings" : "#"}
                className="flex items-center gap-2 px-3 py-1.5 mb-1 rounded-lg hover:bg-secondary transition-colors"
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground shrink-0">
                  {(companyName || user?.email || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  {actualRole === "supplier" && companyName && (
                    <span className="block text-xs font-medium text-foreground truncate">{companyName}</span>
                  )}
                  <span className="block text-[11px] text-muted-foreground truncate">{user?.email}</span>
                </div>
                {actualRole === "admin" && <Settings className="h-3.5 w-3.5 text-muted-foreground" />}
              </Link>
              <button
                onClick={signOut}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
              >
                <LogOut className="h-[18px] w-[18px]" />
                Sign Out
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
};
